import React, {ReactNode, createContext, useContext, useEffect, useState} from 'react';
import styled, {keyframes} from 'styled-components';
import {Notification} from '@xsolla/uikit';

export enum ToastType {
  Success,
  Error
}

interface Toast {
  id: string;
  content: string;
  type: ToastType;
}

interface ToastProviderProps {
  children: ReactNode;
}

const toastAnimation = keyframes`
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/**
 * Return props for the uikit notification component.
 * @param type
 */
const mappedUikitNotificationProps = (type: ToastType) => {
  switch (type) {
    case ToastType.Error:
      return {icon: 'exclamation-alert', status: 'error'};
    case ToastType.Success:
      return {icon: 'checkmark', status: 'success'};
  }
};

const ToastContext = createContext({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  addToast: (content: string, type: ToastType) => {
    return;
  }
});

const ToastContainer = styled.div`
  position: fixed;
  right: 16px;
  top: 16px;
  user-select: none;
  z-index: 2000;
`;

const ToastWrapper = styled.div`
  background-color: #ffffff;
  border-radius: 4px;
  margin-bottom: 8px;
  animation: ${toastAnimation} ease-in-out 80ms;

  &:last-child {
    margin-bottom: 0;
  }
`;

/**
 * Provider for toasts.
 */
export const ToastProvider = ({children}: ToastProviderProps) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const onAddToast = (content: string, type: ToastType) => {
    const toast = {id: toasts.length.toString(), content, type};
    setToasts([...toasts, toast]);
  };

  const onDismissToast = (id: string) => {
    setToasts(t => t.filter(t => t.id !== id));
  };

  const listenTimeoutRemove = (toast: Toast | undefined) => {
    if (!toast) return;
    setTimeout(() => {
      onDismissToast(toast.id);
    }, 5000);
  };

  useEffect(() => {
    listenTimeoutRemove(toasts[toasts.length - 1]);
  }, [toasts]);

  return (
    <ToastContext.Provider value={{addToast: onAddToast}}>
      {children}
      <ToastContainer>
        {toasts.map(({content, type, id}) => (
          <ToastWrapper key={`toast-${id}`}>
            <Notification
              {...mappedUikitNotificationProps(type)}
              float={true}
              title={content}
              onClose={() => onDismissToast(id)}
            />
          </ToastWrapper>
        ))}
      </ToastContainer>
    </ToastContext.Provider>
  );
};

/**
 * Hook for using methods from `ToastContext`.
 */
export const useToasts = () => useContext(ToastContext);
